/**
 * LCHP soft-challenge modal. Built lazily on demand; nothing in the DOM at
 * page load until `run()` is called. The checkbox itself is not in the DOM
 * during the countdown — it's injected only when the countdown reaches zero.
 *
 * Public API:
 *   LchpChallenge.run(challengeData, ajaxUrl) -> Promise<boolean>
 *     resolves true if the challenge passed, false otherwise (cancel/fail).
 *
 * On a successful resolution the server has set a one-shot bypass flag in
 * the session. The caller is expected to re-submit the original form body
 * to the host endpoint — the next evaluateData() call will skip the
 * honeypot check and the submission goes through.
 */
export const LchpChallenge = new (class {
  #activePanel = null;

  async run(challengeData, ajaxUrl) {
    if (this.#activePanel) {
      return false;
    }

    return new Promise((resolve) => {
      this.#open(challengeData, ajaxUrl, resolve);
    });
  }

  #open(challengeData, ajaxUrl, resolve) {
    const { challenge_token, selectors } = challengeData;
    const i18n = (typeof window !== 'undefined' && window.lexocaptchaFrontLocalized?.i18n) || {};
    const t = (key, fallback) => (i18n[key] || fallback);

    const behavior = {
      mouse_moves: 0,
      focus_count: 0,
      hover_dwell_ms: 0,
      time_to_click_ms: 0,
    };
    let checkboxRevealAt = null;
    let hoverEnter = null;
    let beaconSent = false;

    const root = document.createElement('div');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.className = 'lchp-challenge';
    const closeLabel = t('close', 'Close');
    const title      = t('quick_verification', 'Quick verification');
    const intro      = t('verification_intro', "Please confirm you're human to complete your submission.");
    root.innerHTML = `
      <div class="lchp-challenge__backdrop"></div>
      <div class="lchp-challenge__panel" tabindex="-1">
        <button type="button" class="lchp-challenge__close" aria-label="${closeLabel}">&times;</button>
        <h2 class="lchp-challenge__title"></h2>
        <p class="lchp-challenge__body"></p>
        <div class="lchp-challenge__countdown" aria-live="polite"></div>
        <div class="lchp-challenge__slot"></div>
      </div>
    `;
    root.querySelector('.lchp-challenge__title').textContent = title;
    root.querySelector('.lchp-challenge__body').textContent = intro;
    document.body.appendChild(root);
    this.#activePanel = root;

    const panel = root.querySelector('.lchp-challenge__panel');
    const slot = root.querySelector('.lchp-challenge__slot');
    const countdownEl = root.querySelector('.lchp-challenge__countdown');
    const closeBtn = root.querySelector('.lchp-challenge__close');

    const sendCancelBeacon = () => {
      if (beaconSent) return;
      beaconSent = true;
      try {
        const fd = new FormData();
        fd.append('action', 'lexo_captcha_cancel_challenge');
        fd.append('challenge_token', challenge_token);
        navigator.sendBeacon(ajaxUrl, fd);
      } catch (_) {}
    };

    const finish = (outcome, sendCancel = false) => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('beforeunload', onUnload);
      if (sendCancel) sendCancelBeacon();
      root.remove();
      this.#activePanel = null;
      resolve(outcome);
    };

    const focusTrap = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = panel.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        last.focus();
        event.preventDefault();
      } else if (!event.shiftKey && document.activeElement === last) {
        first.focus();
        event.preventDefault();
      }
    };

    const onMouseMove = () => { behavior.mouse_moves++; };
    document.addEventListener('mousemove', onMouseMove);

    const onKey = (e) => {
      if (e.key === 'Escape') {
        finish(false, true);
      } else {
        focusTrap(e);
      }
    };
    document.addEventListener('keydown', onKey);

    const onUnload = () => sendCancelBeacon();
    window.addEventListener('beforeunload', onUnload);

    closeBtn.addEventListener('click', () => finish(false, true));

    panel.focus();

    const countdownFormat = t('countdown', 'Verifying… %d');
    let remaining = 2;
    countdownEl.textContent = countdownFormat.replace('%d', remaining);
    const interval = setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        countdownEl.textContent = countdownFormat.replace('%d', remaining);
        return;
      }
      clearInterval(interval);
      countdownEl.remove();

      const label = document.createElement('label');
      label.className = 'lchp-challenge__checkbox';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.id = selectors.checkbox_id;
      checkbox.name = selectors.checkbox_name;
      const text = document.createElement('span');
      text.textContent = t('im_not_a_robot', "I'm not a robot");
      label.appendChild(checkbox);
      label.appendChild(text);
      slot.appendChild(label);

      checkboxRevealAt = Date.now();

      checkbox.addEventListener('focus', () => { behavior.focus_count++; });
      checkbox.addEventListener('mouseenter', () => { hoverEnter = Date.now(); });
      checkbox.addEventListener('mouseleave', () => {
        if (hoverEnter !== null) {
          behavior.hover_dwell_ms += Date.now() - hoverEnter;
          hoverEnter = null;
        }
      });

      checkbox.addEventListener('change', async () => {
        if (!checkbox.checked) return;
        behavior.time_to_click_ms = Date.now() - checkboxRevealAt;
        checkbox.disabled = true;

        const verifying = document.createElement('div');
        verifying.className = 'lchp-challenge__verifying';
        verifying.setAttribute('aria-live', 'polite');
        verifying.textContent = t('verifying', 'Verifying…');
        slot.appendChild(verifying);

        const fd = new FormData();
        fd.append('action', 'lexo_captcha_submit_challenge');
        fd.append('challenge_token', challenge_token);
        fd.append('modal_behavior', JSON.stringify(behavior));

        try {
          const response = await fetch(ajaxUrl, { method: 'POST', body: fd });
          const json = await response.json();
          finish(!!json.success, false);
        } catch (_) {
          finish(false, false);
        }
      });

      checkbox.focus();
    }, 1000);
  }
})();
